import { useTranslation } from 'module/hooks/use-translation';
import { NodeViewContent } from '@tiptap/react';
import { CustomSectionState } from 'api/custom-section/types';
import { TableDetailsCustomSectionAttributes } from 'api/custom-section/types/table-details';
import { useCallback, useState } from 'react';
import {
  SectionPreview,
  SectionTitle,
} from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-styles';
import { PromptInput } from './prompt-input';
import { TableDetailsPromptType } from '../../../../types/ui-types';
import { TableDetailsSectionComponentProps } from '../types';

export const TableDetailsPromptSection = ({
  node,
  onRemove,
  onDone,
  onUndo,
  onOpen,
  allowResolve,
  contentEditable,
  pageType,
}: TableDetailsSectionComponentProps) => {
  const showPrompt = node.attrs.state === CustomSectionState.Open;
  const constructPrompt = useCallback(
    (attributes: TableDetailsCustomSectionAttributes) => {
      return {
        title: attributes.title,
      };
    },
    [],
  );
  const [prompt, setPrompt] = useState<TableDetailsPromptType>(
    constructPrompt(node.attrs),
  );

  const defaultTitleLabel = useTranslation(
    'rich_text_editor.sections.question_section.title',
  );

  const onGenerate = async (userPrompt: TableDetailsPromptType) => {
    const attributes = {
      ...node.attrs,
      title: userPrompt.title,
      state: CustomSectionState.Closed,
      isTableStatic: allowResolve ? true : false,
    };

    onDone(attributes, getNodeAsPayload());
  };

  const getNodeAsPayload = useCallback(
    (isPreviewOpen?: boolean) => {
      const payload = node.toJSON();

      if (!('content' in payload)) {
        payload.content = [];
      }

      return {
        ...payload,
        attrs: {
          ...payload.attrs,
          isTableStatic: isPreviewOpen ? true : false,
        },
      };
    },
    [node],
  );

  const onClose = useCallback(() => {
    if (node.content?.size === 0) {
      onRemove();

      return;
    }
    setPrompt(constructPrompt(node.attrs));
    onUndo();
  }, [onUndo, node.attrs, constructPrompt, node.content?.size, onRemove]);

  return (
    <div>
      {showPrompt && contentEditable ? (
        <PromptInput
          prompt={prompt}
          setPrompt={setPrompt}
          onGenerate={onGenerate}
          onCancel={() => {
            onRemove();
          }}
          onUndo={onClose}
          allowResolve={allowResolve}
          getNodeAsPayload={getNodeAsPayload}
          pageType={pageType}
        />
      ) : (
        <SectionPreview
          onClick={() => {
            onOpen();
          }}
        >
          <div>
            <SectionTitle>{prompt.title || defaultTitleLabel}</SectionTitle>
          </div>
          <NodeViewContent />
        </SectionPreview>
      )}
    </div>
  );
};
